if (!window.PriceRangeCustomInitialized) {
    window.PriceRangeCustomInitialized = true;

    class PriceRangeCustom {
        constructor(containerElement) {
            this.container = containerElement;
            
            this.isMobile = this.container.closest('#mobile-filter-form') !== null || 
                           this.container.closest('[x-show="filterModal"]') !== null;
            
            this.filterId = this.container.getAttribute('data-filter-id') || 
                           `filter-${Math.random().toString(36).substring(2, 7)}`;
            
            this.valueChanged = {
                min: false,
                max: false
            };
            
            this.findElements();
            
            if (!this.hasRequiredElements()) {
                console.error(`Required elements not found in price filter ${this.filterId}`, {
                    container: this.container,
                    minRange: !!this.minRange,
                    maxRange: !!this.maxRange,
                    minInput: !!this.minInput,
                    maxInput: !!this.maxInput,
                    range: !!this.range,
                    track: !!this.track
                });
                return;
            }
            
            this.min = parseInt(this.minRange.min);
            this.max = parseInt(this.maxRange.max);
            
            this.initializeValues();
            this.updateSlider();
            this.buildPresets();
            this.bindEvents();

            this.container.setAttribute('data-initialized', 'true');
        }
        
        findElements() {
            this.minRange = this.container.querySelector('.thumb--left');
            this.maxRange = this.container.querySelector('.thumb--right');
            
            const inputs = this.container.querySelectorAll('input[type="text"]');
            this.minInput = inputs[0];
            this.maxInput = inputs[1];
            
            this.range = this.container.querySelector('.slider-range');
            this.track = this.container.querySelector('.slider-track');
        }
        
        hasRequiredElements() {
            return this.minRange && this.maxRange && 
                  this.minInput && this.maxInput && 
                  this.range && this.track;
        }
        
        initializeValues() {
            // Move the slider in 10 TL increments (10, 20, 30 ...). Values are
            // stored in cents, so a 10 TL step = 1000.
            this.minRange.step = 1000;
            this.maxRange.step = 1000;

            let minValue = parseInt(this.minRange.value);
            let maxValue = parseInt(this.maxRange.value);

            this.minInput.value = this.formatNumber(minValue);
            this.maxInput.value = this.formatNumber(maxValue);
            
            this.minRange.value = minValue;
            this.maxRange.value = maxValue;

            // Baseline used to skip redundant filter reloads when focus leaves
            // the filter without the values actually changing.
            this.lastSubmitted = { min: minValue, max: maxValue };
        }

        parseCurrency(value) {
            const parsed = parseInt(value.toString().replace(/[^\d]/g, ''));
            return isNaN(parsed) ? 0 : parsed;
        }
        
        bindEvents() {
            this.minRange.addEventListener('input', () => this.handleRangeInput());
            this.maxRange.addEventListener('input', () => this.handleRangeInput());
            
            this.minInput.addEventListener('input', (e) => this.handleInputChange(e));
            this.maxInput.addEventListener('input', (e) => this.handleInputChange(e));

            // Commit (validate + apply the filter) only when focus leaves the
            // whole price filter — NOT when moving between the min and max
            // inputs or onto a preset button — so the user can fill in both
            // fields before it applies. We schedule the commit on focusout and
            // cancel it if focus returns anywhere inside the filter within a
            // short grace period. This is more reliable than checking
            // relatedTarget/activeElement, which can momentarily be null/body
            // while a click moves focus between elements.
            let blurCommitTimer = null;
            this.container.addEventListener('focusin', () => {
                if (blurCommitTimer) {
                    clearTimeout(blurCommitTimer);
                    blurCommitTimer = null;
                }
            });
            this.container.addEventListener('focusout', () => {
                if (blurCommitTimer) clearTimeout(blurCommitTimer);
                blurCommitTimer = setTimeout(() => {
                    blurCommitTimer = null;
                    // Belt-and-suspenders: only apply if focus truly left the
                    // filter (not the min/max inputs or a preset button).
                    if (!this.container.contains(document.activeElement)) {
                        this.commitInputChange();
                    }
                }, 250);
            });

            const handleEnterKey = (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    if (blurCommitTimer) {
                        clearTimeout(blurCommitTimer);
                        blurCommitTimer = null;
                    }
                    // Apply immediately on Enter.
                    e.target.blur();
                    this.commitInputChange();
                }
            };
            this.minInput.addEventListener('keydown', handleEnterKey);
            this.maxInput.addEventListener('keydown', handleEnterKey);

            // Predefined price ranges (e.g. 500 - 1.000 TL) for quick selection.
            const presetButtons = this.container.querySelectorAll('.price-preset');
            presetButtons.forEach((button) => {
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.applyPreset(button);
                });
            });
            
            const form = this.container.closest('form');
            if (form) {
                const applyButton = this.container.querySelector('button[type="button"]:not(.price-preset), button[type="submit"]:not(.price-preset)');
                if (applyButton) {
                    applyButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.submitWithAjax(form);
                    });
                }
                
                form.addEventListener('submit', (e) => {
                    if (form.hasAttribute('data-ajax-filtering') && form.getAttribute('data-ajax-filtering') === 'true') {
                        e.preventDefault();
                        this.submitWithAjax(form);
                    }
                });
                
                this.minRange.addEventListener('change', () => this.submitWithAjax(form));
                this.maxRange.addEventListener('change', () => this.submitWithAjax(form));
            } else {
                console.warn(`No form found for price filter ${this.filterId}`);
            }
        }
        
        submitWithAjax(form) {
            const minValue = this.parseCurrency(this.minInput.value);
            const maxValue = this.parseCurrency(this.maxInput.value);
            
            this.minInput.value = Math.floor(minValue);
            this.maxInput.value = Math.ceil(maxValue);
            
            form.setAttribute('data-price-filters-changed', 'true');
            
            if (minValue > 0) {
                this.valueChanged.min = true;
                this.minInput.setAttribute('data-value-changed', 'true');
            } else {
                this.valueChanged.min = false;
                this.minInput.setAttribute('data-value-changed', 'false');
            }
            
            const maxDefault = parseInt(this.maxRange.max) || 1000000;
            if (maxValue < maxDefault) {
                this.valueChanged.max = true;
                this.maxInput.setAttribute('data-value-changed', 'true');
            } else {
                this.valueChanged.max = false;
                this.maxInput.setAttribute('data-value-changed', 'false');
            }
            
            const hollyFilters = window.hollyFilters;
            if (hollyFilters && typeof hollyFilters.handleFormSubmit === 'function') {
                hollyFilters.handleFormSubmit(form, new Event('submit'));
            } else {
                const filterContainer = document.querySelector('filter-container');
                if (filterContainer && typeof filterContainer.ajaxLoadForm === 'function') {
                    filterContainer.ajaxLoadForm({ preventDefault: () => {}, currentTarget: form });
                } else {
                    form.submit();
                }
            }
        }
        
        loadUrlWithAjax(url) {
            const filterContainer = document.querySelector('filter-container');
            if (filterContainer && typeof filterContainer.ajaxLoadUrl === 'function') {
                filterContainer.ajaxLoadUrl(url);
            } else {
                window.location.href = url;
            }
        }
        
        handleRangeInput() {
            let minValue = parseInt(this.minRange.value);
            let maxValue = parseInt(this.maxRange.value);
            
            if (minValue > maxValue) {
                if (this.minRange === document.activeElement) {
                    maxValue = minValue;
                    this.maxRange.value = maxValue;
                } else {
                    minValue = maxValue;
                    this.minRange.value = minValue;
                }
            }
            
            const minDefault = parseInt(this.minRange.min) || 0;
            const maxDefault = parseInt(this.maxRange.max) || 1000000;
            
            const form = this.container.closest('form');
            
            if (minValue > minDefault) {
                this.valueChanged.min = true;
                this.minInput.setAttribute('data-value-changed', 'true');
                if (form) form.setAttribute('data-price-filters-changed', 'true');
            } else {
                this.valueChanged.min = false;
                this.minInput.removeAttribute('data-value-changed');
            }
            
            if (maxValue < maxDefault) {
                this.valueChanged.max = true;
                this.maxInput.setAttribute('data-value-changed', 'true');
                if (form) form.setAttribute('data-price-filters-changed', 'true');
            } else {
                this.valueChanged.max = false;
                this.maxInput.removeAttribute('data-value-changed');
            }
            
            if (!this.valueChanged.min && !this.valueChanged.max && form) {
                form.removeAttribute('data-price-filters-changed');
            }
            
            this.minInput.value = this.formatNumber(minValue);
            this.maxInput.value = this.formatNumber(maxValue);
            
            this.updateSlider();
        }
        
        handleInputChange() {
            // While the user is typing we only move the slider visually and
            // clamp to the absolute range bounds. We intentionally do NOT
            // clamp min/max against each other here, otherwise starting to
            // type a new max value (e.g. "1...") would momentarily reset the
            // min. We also avoid reformatting the field so the caret/value the
            // user is typing is preserved. The full validation runs on commit.
            let minValue = this.parseCurrency(this.minInput.value) * 100;
            let maxValue = this.parseCurrency(this.maxInput.value) * 100;

            minValue = Math.max(this.min, Math.min(minValue, this.max));
            maxValue = Math.max(this.min, Math.min(maxValue, this.max));

            this.minRange.value = minValue;
            this.maxRange.value = maxValue;

            this.updateSlider();
        }

        commitInputChange() {
            let minValue = this.parseCurrency(this.minInput.value) * 100;
            let maxValue = this.parseCurrency(this.maxInput.value) * 100;

            // Now that editing is finished, apply the full min/max validation.
            minValue = Math.max(this.min, Math.min(minValue, maxValue));
            maxValue = Math.min(this.max, Math.max(maxValue, minValue));

            const minDefault = parseInt(this.minRange.min) || 0;
            const maxDefault = parseInt(this.maxRange.max) || 1000000;

            const form = this.container.closest('form');

            if (minValue > minDefault) {
                this.valueChanged.min = true;
                this.minInput.setAttribute('data-value-changed', 'true');
                if (form) form.setAttribute('data-price-filters-changed', 'true');
            } else {
                this.valueChanged.min = false;
                this.minInput.removeAttribute('data-value-changed');
            }

            if (maxValue < maxDefault) {
                this.valueChanged.max = true;
                this.maxInput.setAttribute('data-value-changed', 'true');
                if (form) form.setAttribute('data-price-filters-changed', 'true');
            } else {
                this.valueChanged.max = false;
                this.maxInput.removeAttribute('data-value-changed');
            }

            if (!this.valueChanged.min && !this.valueChanged.max && form) {
                form.removeAttribute('data-price-filters-changed');
            }

            this.minRange.value = minValue;
            this.maxRange.value = maxValue;

            this.minInput.value = this.formatNumber(minValue);
            this.maxInput.value = this.formatNumber(maxValue);

            this.updateSlider();

            // Only reload if the committed range actually changed.
            if (form && (minValue !== this.lastSubmitted.min || maxValue !== this.lastSubmitted.max)) {
                this.lastSubmitted = { min: minValue, max: maxValue };
                this.submitWithAjax(form);
            }
        }

        buildPresets() {
            const presetContainer = this.container.querySelector('.price-presets');
            if (!presetContainer) return;

            // Build 3 evenly-sized buckets from this category's max price so the
            // ranges always fit the catalogue (e.g. a 3.000 TL category gets
            // 0-1.000 / 1.000-2.000 / 2.000-3.000, a 6.000 TL one gets
            // 0-2.000 / 2.000-4.000 / 4.000-6.000). this.max is in cents.
            const maxTl = Math.round(this.max / 100);
            if (!maxTl || maxTl <= 0) return;

            // Bucket size = max / 3, rounded up to a tidy 500 TL increment.
            let step = Math.ceil(maxTl / 3 / 500) * 500;
            if (step < 500) step = 500;

            const buttonClass = 'price-preset hp-text-xs hp-text-dgray hp-border hp-border-[#EEEEEE] hp-rounded hp-px-2 hp-py-1 hover:hp-border-dgray hp-duration-300';

            // Replace the static fallback buttons with the dynamic ones.
            presetContainer.innerHTML = '';
            for (let i = 0; i < 3; i++) {
                const minTl = step * i;
                const roundedTop = step * (i + 1);

                // For the last bucket, if its rounded top would exceed the real
                // max price, make it open-ended ("X TL ve üzeri") and cap the
                // applied value at the real max — so the label never shows a
                // price higher than anything in the catalogue, with no gap.
                const isOpenEnded = i === 2 && roundedTop > maxTl;
                const maxTlBucket = isOpenEnded ? maxTl : roundedTop;

                const button = document.createElement('button');
                button.type = 'button';
                button.className = buttonClass;
                button.setAttribute('data-price-min', minTl);
                button.setAttribute('data-price-max', maxTlBucket);
                button.textContent = isOpenEnded
                    ? `${this.formatTL(minTl)} TL ve üzeri`
                    : `${this.formatTL(minTl)} - ${this.formatTL(maxTlBucket)} TL`;
                presetContainer.appendChild(button);
            }
        }

        formatTL(value) {
            return Number(value).toLocaleString('tr-TR');
        }

        applyPreset(button) {
            const presetMin = button.getAttribute('data-price-min');
            const presetMax = button.getAttribute('data-price-max');

            if (presetMin !== null) {
                this.minInput.value = presetMin;
            }
            if (presetMax !== null) {
                this.maxInput.value = presetMax;
            }

            // Reuse the same validate + apply path as manual input.
            this.commitInputChange();
        }

        formatNumber(value) {
            const wholeNumber = Math.round(value / 100);
            const formatted = wholeNumber.toLocaleString('tr-TR');
            return formatted;
        }
        
        updateSlider() {
            const percent1 = ((this.minRange.value - this.min) / (this.max - this.min)) * 100;
            const percent2 = ((this.maxRange.value - this.min) / (this.max - this.min)) * 100;
            
            this.range.style.left = percent1 + '%';
            this.range.style.width = Math.max(0, percent2 - percent1) + '%';
        }
    }
    
    function initializeAllPriceFilters() {
        const priceFilterContainers = document.querySelectorAll('.price-filter');
        
        if (priceFilterContainers.length > 0) {
            
            priceFilterContainers.forEach(container => {
                if (container.getAttribute('data-initialized') !== 'true') {
                    new PriceRangeCustom(container);
                }
            });
        }
    }
    
    function setupPriceFilterObserver() {
        const observer = new MutationObserver(mutations => {
            let shouldInitialize = false;
            
            mutations.forEach(mutation => {
                if (mutation.type === 'childList' && mutation.addedNodes.length) {
                    mutation.addedNodes.forEach(node => {
                        if (node.nodeType === 1) {
                            if (node.classList?.contains('price-filter') || 
                                (node.querySelectorAll && node.querySelectorAll('.price-filter').length > 0)) {
                                shouldInitialize = true;
                            }
                        }
                    });
                }
            });
            
            if (shouldInitialize) {
                setTimeout(initializeAllPriceFilters, 100);
            }
        });
        
        observer.observe(document.body, { childList: true, subtree: true });
    }
    
    function setupAlpineIntegration() {
        if (window.Alpine) {
            document.addEventListener('alpine:init', () => {
                Alpine.data('filterModal', () => ({
                    open: false,
                    init() {
                        this.$watch('open', value => {
                            if (value) {
                                this.$nextTick(() => {
                                    setTimeout(() => {
                                        initializeAllPriceFilters();
                                    }, 300);
                                });
                            }
                        });
                    }
                }));
            });
        }
    }
    
    document.addEventListener('DOMContentLoaded', () => {
        initializeAllPriceFilters();
        setupPriceFilterObserver();
        setupAlpineIntegration();
    });
    
    window.addEventListener('load', () => {
        setTimeout(initializeAllPriceFilters, 500);
    });

    document.addEventListener('shopify:section:load', (event) => {
        setTimeout(initializeAllPriceFilters, 100);
    });
}